import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { useStore } from "@/lib/store";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, ShoppingBag, Users, TrendingUp, Megaphone, Trash2, Edit } from "lucide-react";

export default function Admin() {
  const { user } = useStore();
  const [tab, setTab] = useState("dashboard");
  if (!user) return <Navigate to="/auth" replace/>;
  if (!user.is_admin) return <Navigate to="/" replace/>;

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
        <div>
          <div className="eyebrow">The Atelier</div>
          <h1 className="font-serif-luxe text-4xl text-[#FAF8F5] mt-2">Laxmi Aura Admin</h1>
        </div>
        <div className="text-xs tracking-[0.24em] uppercase text-[#C2B29F] mt-2 md:mt-0">Signed in as {user.name}</div>
      </div>
      <div className="flex gap-2 border-b border-[#D4AF37]/15 mb-8 overflow-x-auto">
        {[
          { k: "dashboard", l: "Dashboard" },
          { k: "products", l: "Products" },
          { k: "orders", l: "Orders" },
          { k: "announcement", l: "Announcement" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
            className={`px-4 py-3 text-[11px] tracking-[0.24em] uppercase transition ${tab === t.k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#C2B29F] hover:text-[#FAF8F5]"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "dashboard" && <Dashboard/>}
      {tab === "products" && <Products/>}
      {tab === "orders" && <Orders/>}
      {tab === "announcement" && <AnnouncementEditor/>}
    </div>
  );
}

const Dashboard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  if (!s) return <div className="text-[#C2B29F]">Loading…</div>;
  const cards = [
    { i: TrendingUp, l: "Revenue", v: formatINR(s.revenue) },
    { i: ShoppingBag, l: "Orders", v: s.total_orders },
    { i: Package, l: "Products", v: s.total_products },
    { i: Users, l: "Customers", v: s.total_users },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((c, i) => (
        <div key={i} className="bg-[#1C130E] border border-[#D4AF37]/20 p-6 gold-glow">
          <c.i className="text-[#D4AF37] mb-3" size={20}/>
          <div className="eyebrow">{c.l}</div>
          <div className="font-serif-luxe text-3xl text-[#FAF8F5] mt-2">{c.v}</div>
        </div>
      ))}
    </div>
  );
};

const emptyForm = { name: "", slug: "", category: "rings", metal: "22K Gold", gemstone: "", price: 0, description: "", images: [""], stock: 10 };
const Products = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/products", { params: { limit: 200 } }).then(r => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0, images: (form.images || []).filter(Boolean) };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved");
      setForm(null);
      load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <div className="text-sm text-[#C2B29F]">{products.length} products</div>
        <button onClick={() => setForm(emptyForm)} data-testid="new-product" className="btn-outline-gold !py-2 !px-4">+ New Product</button>
      </div>
      <div className="overflow-x-auto bg-[#1C130E] border border-[#D4AF37]/15">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.24em] uppercase text-[#C2B29F] border-b border-[#D4AF37]/15">
            <tr><th className="text-left p-4">Product</th><th className="text-left p-4">Category</th><th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id} className="border-b border-[#D4AF37]/10 hover:bg-[#221712]">
                <td className="p-4 flex items-center gap-3"><img src={p.images?.[0]} className="w-10 h-10 object-cover"/><span className="text-[#FAF8F5]">{p.name}</span></td>
                <td className="p-4 text-[#C2B29F] capitalize">{p.category}</td>
                <td className="p-4 text-[#D4AF37]">{formatINR(p.price)}</td>
                <td className="p-4 text-[#C2B29F]">{p.stock}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setForm({...p, images: p.images || [""]})} className="text-[#C2B29F] hover:text-[#D4AF37] mr-3"><Edit size={14}/></button>
                  <button onClick={() => remove(p.id)} className="text-[#C2B29F] hover:text-red-400"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={(e) => e.target === e.currentTarget && setForm(null)}>
          <form onSubmit={save} className="bg-[#1C130E] border border-[#D4AF37]/30 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="font-serif-luxe text-2xl text-[#FAF8F5] mb-6">{form.id ? "Edit Product" : "New Product"}</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <F l="Name" v={form.name} onChange={v => setForm({...form, name: v})}/>
              <F l="Slug" v={form.slug} onChange={v => setForm({...form, slug: v})}/>
              <F l="Category" v={form.category} onChange={v => setForm({...form, category: v})}/>
              <F l="Metal" v={form.metal} onChange={v => setForm({...form, metal: v})}/>
              <F l="Gemstone" v={form.gemstone || ""} onChange={v => setForm({...form, gemstone: v})}/>
              <F l="Price" v={form.price} onChange={v => setForm({...form, price: v})}/>
              <F l="Stock" v={form.stock} onChange={v => setForm({...form, stock: v})}/>
              <F l="Image URL" v={(form.images && form.images[0]) || ""} onChange={v => setForm({...form, images: [v, ...(form.images || []).slice(1)]})}/>
              <div className="col-span-2"><F l="Description" v={form.description} onChange={v => setForm({...form, description: v})} textarea/></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button className="btn-gold">Save</button>
              <button type="button" onClick={() => setForm(null)} className="btn-outline-gold">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const F = ({ l, v, onChange, textarea }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.14em] uppercase text-[#C2B29F]">{l}</span>
    {textarea ? (
      <textarea rows="3" value={v} onChange={e => onChange(e.target.value)} className="mt-1 w-full bg-transparent border border-[#D4AF37]/25 px-3 py-2 text-[#FAF8F5] focus:border-[#D4AF37] focus:outline-none"/>
    ) : (
      <input value={v} onChange={e => onChange(e.target.value)} className="mt-1 w-full bg-transparent border border-[#D4AF37]/25 px-3 py-2 text-[#FAF8F5] focus:border-[#D4AF37] focus:outline-none"/>
    )}
  </label>
);

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const updateStatus = async (id, status) => { await api.put(`/orders/${id}/status`, { status }); toast.success("Updated"); load(); };
  return (
    <div className="overflow-x-auto bg-[#1C130E] border border-[#D4AF37]/15">
      <table className="w-full text-sm">
        <thead className="text-[10px] tracking-[0.24em] uppercase text-[#C2B29F] border-b border-[#D4AF37]/15">
          <tr><th className="text-left p-4">Order</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Items</th><th className="text-left p-4">Total</th><th className="text-left p-4">Status</th></tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id} className="border-b border-[#D4AF37]/10 hover:bg-[#221712]">
              <td className="p-4 text-[#D4AF37]">#{o.id.slice(0,8).toUpperCase()}</td>
              <td className="p-4 text-[#FAF8F5]">{o.customer_name}<br/><span className="text-xs text-[#C2B29F]">{o.email}</span></td>
              <td className="p-4 text-[#C2B29F]">{o.items.length}</td>
              <td className="p-4 text-[#FAF8F5]">{formatINR(o.total)}</td>
              <td className="p-4">
                <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} className="bg-transparent border border-[#D4AF37]/30 px-2 py-1 text-xs text-[#FAF8F5]">
                  {["confirmed","processing","shipped","delivered","cancelled"].map(s => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#C2B29F]">No orders yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const AnnouncementEditor = () => {
  const [a, setA] = useState({ text: "", link: "", enabled: true });
  useEffect(() => { api.get("/announcement").then(r => setA(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    await api.put("/announcement", a);
    toast.success("Announcement updated");
  };
  return (
    <form onSubmit={save} className="bg-[#1C130E] border border-[#D4AF37]/20 p-8 max-w-2xl gold-glow">
      <Megaphone className="text-[#D4AF37] mb-4" size={22}/>
      <div className="font-serif-luxe text-2xl text-[#FAF8F5]">Announcement Bar</div>
      <p className="text-xs text-[#C2B29F] mt-1">The marquee shown at the top of every page.</p>
      <div className="mt-6 space-y-4">
        <F l="Announcement text" v={a.text || ""} onChange={v => setA({...a, text: v})}/>
        <F l="Optional link" v={a.link || ""} onChange={v => setA({...a, link: v})}/>
        <label className="flex items-center gap-3 text-sm text-[#FAF8F5]">
          <input type="checkbox" checked={a.enabled} onChange={e => setA({...a, enabled: e.target.checked})} className="accent-[#D4AF37]"/>
          Show announcement bar
        </label>
      </div>
      <button className="btn-gold mt-6">Save</button>
    </form>
  );
};
